
export const secondsToFormattedString = (s: number) => {
  const minutes = Math.floor(s / 60);
  const seconds = s - (minutes * 60);
  return `${minutes > 0 ? `${minutes}` : `0`}:${seconds < 10 ? `0${seconds}` : seconds}`
}

export const SECOND = 1000;
export const MINUTE = SECOND * 60;
export const HOUR = MINUTE * 60;
export const DAY = HOUR * 24;

export const daysAfterTimestamp = (timestamp: number) => Math.floor((Date.now() - timestamp) / DAY)

export const daysAfterDate = (dateString: string) => daysAfterTimestamp(new Date(dateString).getTime())

export const convert24hourTo12hour = (hours: number, mins?: number) => {
  const period = hours < 12 ? 'AM' : 'PM';
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'

  return `${hours}${mins !== undefined ? `:${mins.toString().padStart(2, '0')}` : ``}\u00A0${period}`;
}

export const addHours = (date: Date, hours: number) => {
  return new Date(date.getTime() + (hours * HOUR));
}

export const addMinutes = (date: Date, minutes: number) => {
  return new Date(date.getTime() + (minutes * MINUTE));
}

export const getDateString = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export const formatDateString = (dateString?: string) => {
  if (!dateString) return '';
  return dateString.split('-').reverse().join('/');
}

export const getTimeString = (date: Date) => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
}

export const mergeDateAndTime = (date: Date, time: Date) => {
  // Get the date components
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  // Get the time components
  const hours = time.getHours();
  const minutes = time.getMinutes();
  const seconds = time.getSeconds();
  const milliseconds = time.getMilliseconds();

  // Create a new date with the date component from the first
  // and the time component from the second
  return new Date(year, month, day, hours, minutes, seconds, milliseconds);
}

export const stripTime = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const isDateInRange = (target: Date, start: Date, end: Date) => {
  if (!target || !start || !end) return false;

  const targetDate = stripTime(target);
  const startDate = stripTime(start);
  const endDate = stripTime(end);

  return targetDate.getTime() >= startDate.getTime() && targetDate.getTime() <= endDate.getTime();
}

export const isDateBefore = (target: Date, comparison: Date) => {
  if (!target || !comparison) return false;

  const targetDate = stripTime(target);
  const comparisonDate = stripTime(comparison);

  return targetDate.getTime() < comparisonDate.getTime();
}

export const isDateAfter = (target: Date, comparison: Date) => {
  if (!target || !comparison) return false;

  const targetDate = stripTime(target);
  const comparisonDate = stripTime(comparison);

  return targetDate.getTime() > comparisonDate.getTime();
}

export const isSameDay = (date1: Date, date2: Date) => {
  if (!date1 || !date2) return false;
  return stripTime(date1).getTime() === stripTime(date2).getTime();
}

export const setHours = (date: Date, hours: number) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours);

export const roundUpToNextHour = (date: Date) => {
  const newDate = new Date(date.getTime());
  if (newDate.getMinutes() === 0) {
    // If the date is already at the beginning of an hour, return it as is
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);

    return newDate;
  } else {
    // Set minutes, seconds, and milliseconds to zero
    newDate.setMinutes(0);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);

    // Add one hour
    newDate.setHours(newDate.getHours() + 1);
  }

  return newDate;
}

export const updateToWorkingHours = (date: Date, min: number, max: number) => {
  const newDate = new Date(date.getTime());
  if (newDate.getHours() < min) {
    newDate.setHours(min);
  } else if (newDate.getHours() > max) {
    newDate.setDate(newDate.getDate() + 1);
    newDate.setHours(min);
  }

  return newDate;
}

export const getTimezoneOffset = (timeZone: string) => {
  // Get the current UTC time
  const now = new Date();
  const utcTime = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(),
    now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds());

  // Format the current UTC time in the specified timezone
  const formattedTimeInTimezone = new Intl.DateTimeFormat('en-US', {
    timeZone: timeZone,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(new Date(utcTime));

  const year = now.getUTCFullYear();
  const month = now.getUTCMonth() + 1;
  const day = now.getUTCDate();

  const monthFormatted = month < 10 ? `0${month}` : month;
  const dayFormatted = day < 10 ? `0${day}` : day;

  // Convert formatted time to a Date object
  // const localTimeInTimezone = new Date(`${now.getUTCFullYear()}-${now.getUTCMonth() + 1}-${now.getUTCDate()} ${formattedTimeInTimezone} UTC`);

  const localDateString = `${year}-${monthFormatted}-${dayFormatted}T${formattedTimeInTimezone}Z`;

  // const localTimeInTimezone = new Date(`${now.getUTCFullYear()}-${now.getUTCMonth() + 1}-${now.getUTCDate()} ${formattedTimeInTimezone} UTC`);

  const localTimeInTimezone = new Date(localDateString);
  const localTimestampInTimezone = localTimeInTimezone.getTime();

  return (localTimestampInTimezone - utcTime) / 60000;
}

export const localDateToUTCString = (date: Date, timeZone: string) => {
  const offset = getTimezoneOffset(timeZone);

  // Calculate the UTC time by adjusting the local time by the offset
  let utcDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000) - (offset * 60000));

  // Format the UTC date
  let utcYear = utcDate.getUTCFullYear();
  let utcMonth = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
  let utcDay = String(utcDate.getUTCDate()).padStart(2, '0');
  let utcHour = String(utcDate.getUTCHours()).padStart(2, '0');
  let utcMinute = String(utcDate.getUTCMinutes()).padStart(2, '0');
  let utcSecond = String(utcDate.getUTCSeconds()).padStart(2, '0');

  // Construct the UTC time string
  return `${utcYear}-${utcMonth}-${utcDay}T${utcHour}:${utcMinute}:${utcSecond}Z`;
}

export const utcStringToLocalDate = (string: string, timeZone: string) => {
  const offset = getTimezoneOffset(timeZone);
  const date = new Date(string);
  return new Date(date.getTime() + (date.getTimezoneOffset() * 60000) + (offset * 60000));
}

export const dateToString = (date: Date) => {
  let year = date.getFullYear();
  let month = String(date.getMonth() + 1).padStart(2, '0');
  let day = String(date.getUTCDate()).padStart(2, '0');
  let hour = String(date.getHours()).padStart(2, '0');
  let minute = String(date.getMinutes()).padStart(2, '0');
  let second = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}T${hour}:${minute}:${second}Z`;
}

export const stringToDate = (string: string) => {
  const date = new Date(string);
  return new Date(date.getTime() + (date.getTimezoneOffset() * 60000));
}